import React, { useEffect, useCallback, useState } from 'react';
import { Form, FormRow, Loading, Row, Col } from '@op/opds.opux';
import { SearchResults } from '../components/SearchResults';
import ErrorNotification from '../components/ErrorNotification';
import { AutoSuggestOption } from '../utils/types';
import { SearchBox } from '../components/SearchBox';
import { AutoSuggestOptions } from '../components/AutoSuggestOptions';
import { SearchWarning } from '../components/SearchWarning';
import { useAppContext } from '../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import { useOfficeApi } from '../hooks/useOfficeApi';
import { useTranslation } from 'react-i18next';

export const SearchForm: React.FC = () => {
  const { t } = useTranslation();

  const {
    query,
    autoSuggestOptions,
    loading,
    shouldFetchAutosuggest,
    officeResults,
    error,
    setLoading,
    setError,
    setAutoSuggestOptions,
    setOfficeResults,
    setQuery,
    setShouldFetchAutosuggest,
    setOfficeDetails
  } = useAppContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const [fetchingOfficeDetails, setFetchingOfficeDetails] = useState(false);
  const [rawSuggestions, setRawSuggestions] = useState<AutoSuggestOption[]>([]);

  const officeApi = useOfficeApi({
    setAutoSuggestOptions,
    setOfficeResults,
    setOfficeDetails
  });

  // Handle URL parameters on page load
  useEffect(() => {
    const urlQuery = searchParams.get('query');
    if (urlQuery && urlQuery !== query) {
      setQuery(urlQuery);
      if (urlQuery.length >= 3) {
        handleSearch(urlQuery);
      }
    }
  }, []);

  useEffect(() => {
    // Sanitize + normalize whitespace for autosuggest
    const sanitizedQuery = query
      .replace(/[^a-zA-Z0-9äöåÄÖÅ\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (sanitizedQuery.length >= 3 && shouldFetchAutosuggest) {
      // Extract the first word or compound word (with hyphens) for API call
      // This allows suggestions to work even when typing additional words
      const words = sanitizedQuery.split(/\s+/);
      const firstWord = words[0];

      // Only fetch if first word is at least 3 characters
      if (firstWord.length >= 3) {
        handleAutosuggest(sanitizedQuery, firstWord);
      }
    } else if (sanitizedQuery.length < 3) {
      setRawSuggestions([]);
      setAutoSuggestOptions([]);
    }
  }, [query, shouldFetchAutosuggest]);

  // Filter raw suggestions based on the full query
  useEffect(() => {
    const sanitizedQuery = query
      .replace(/[^a-zA-Z0-9äöåÄÖÅ\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    if (rawSuggestions.length > 0 && sanitizedQuery.length >= 3) {
      // Check if query contains a space
      const hasSpace = sanitizedQuery.includes(' ');

      if (hasSpace) {
        // If user typed a space, check if suggestions contain space
        // If they do, filter normally; if they don't, keep all suggestions
        const words = sanitizedQuery.split(/\s+/);
        const firstWord = words[0];

        const filtered = rawSuggestions.filter(suggestion => {
          const suggestionLower = suggestion.term.toLowerCase();
          // If suggestion contains space, match full query
          if (suggestionLower.includes(' ')) {
            return suggestionLower.startsWith(sanitizedQuery);
          }
          // If suggestion has no space, just match first word (lock suggestions)
          return suggestionLower.startsWith(firstWord);
        });
        setAutoSuggestOptions(filtered);
      } else {
        // No space in query - normal filtering
        const filtered = rawSuggestions.filter(suggestion =>
          suggestion.term.toLowerCase().startsWith(sanitizedQuery)
        );
        setAutoSuggestOptions(filtered);
      }
    } else if (sanitizedQuery.length < 3) {
      setAutoSuggestOptions([]);
    }
  }, [query, rawSuggestions, setAutoSuggestOptions]);

  const handleSearch = useCallback(
    (searchQuery: string) => {
      setLoading(true);
      setShouldFetchAutosuggest(false);
      // Update URL with search query
      // Sanitize + normalize whitespace before search
      const sanitizedQuery = searchQuery
        .replace(/[^a-zA-Z0-9äöåÄÖÅ\s-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (sanitizedQuery.length === 0) {
        setLoading(false);
        setSearchParams({});
        setOfficeResults([]);
      } else {
        setSearchParams({ query: sanitizedQuery });
        officeApi.fetchOfficeList(sanitizedQuery);
        setError(null);
      }
    },
    [
      setSearchParams,
      officeApi,
      setLoading,
      setShouldFetchAutosuggest,
      setError
    ]
  );

  const handleAutosuggest = useCallback(async (_fullQuery: string, searchTerm: string) => {
    if (!searchTerm) return;
    setOfficeResults([]);
    try {
      // Pass flag indicating if we already have suggestions cached
      const hasExisting = rawSuggestions.length > 0;
      const suggestions = await officeApi.fetchAutoSuggest(searchTerm, hasExisting);
      setRawSuggestions(suggestions || []);
    } catch (err) {
      setRawSuggestions([]);
    }
    setError(null);
  }, [officeApi, setOfficeResults, setRawSuggestions, setError, rawSuggestions]);

  const handleSuggestionClick = useCallback(
    (option: AutoSuggestOption) => {
      if (option?.term) {
        setQuery(option.term);
        setAutoSuggestOptions([]);
        setRawSuggestions([]);
        setShouldFetchAutosuggest(false);
        handleSearch(option.term);
      }
    },
    [handleSearch, setQuery, setAutoSuggestOptions, setRawSuggestions, setShouldFetchAutosuggest]
  );

  const handleQueryChange = useCallback((value: string) => {
    setError(null);
    if (value.length < 3) {
      setAutoSuggestOptions([]);
      setRawSuggestions([]);
    }
    setQuery(value);
    setShouldFetchAutosuggest(true);
  }, [setError, setAutoSuggestOptions, setRawSuggestions, setQuery, setShouldFetchAutosuggest]);

  const handleSelectOffice = useCallback(
    async (
      branchNumber: string,
      branchOrganizationIdentificationNumber: string
    ) => {
      setError(null);
      setFetchingOfficeDetails(true);

      try {
        // Fetch office details first
        await officeApi.fetchOfficeDetails(
          branchNumber,
          branchOrganizationIdentificationNumber
        );

        // Only navigate after successful fetch
        const currentQuery = searchParams.get('query') ?? query;
        setSearchParams({
          query: currentQuery,
          konttorinro: branchNumber,
          konttorioyno: branchOrganizationIdentificationNumber
        });
      } catch (error) {
        /* no-op */
      } finally {
        setFetchingOfficeDetails(false);
      }
    },
    [
      setSearchParams,
      searchParams,
      query,
      setFetchingOfficeDetails,
      setError,
      officeApi
    ]
  );

  return (
    <>
      <Form
        role="search"
        spacing="small"
        onSubmit={e => {
          e.preventDefault();
        }}
        stacked
        style={{ marginTop: 0, paddingTop: 0 }}
      >
        <FormRow required style={{ marginTop: 0, paddingTop: 0 }}>
          <SearchBox
            query={query}
            setQuery={handleQueryChange}
            fetchOfficeResults={handleSearch}
            autoSuggestOptions={autoSuggestOptions}
          />
          {autoSuggestOptions.length > 0 && shouldFetchAutosuggest && (
            <AutoSuggestOptions
              options={autoSuggestOptions}
              handleSuggestionClick={handleSuggestionClick}
            />
          )}
        </FormRow>
      </Form>
      {!loading &&
        !shouldFetchAutosuggest &&
        (officeResults.length >= 50 || officeResults.length === 0) && (
          <Row>
            <Col xs={12} sm={9} lg={9} className="errorPopup">
              <SearchWarning
                message={(() => {
                  if (officeResults.length >= 50) return t('search.result_warning');
                  if (officeResults.length === 0) return t('search.no_results');
                  return '';
                })()}
              />
            </Col>
          </Row>
        )}

      {!loading && error && (
        <Row>
          <Col xs={12} sm={9} lg={9} className="errorPopup">
            <ErrorNotification message={error} />
          </Col>
        </Row>
      )}
      {fetchingOfficeDetails ? (
        <Loading data-testid="office-details-loader" className="loader-style" />
      ) : (
        loading && <Loading data-testid="loader" className="loader-style" />
      )}
      {!loading && officeResults.length > 0 && (
        <SearchResults
          officeResults={officeResults}
          onSelectOffice={handleSelectOffice}
        />
      )}
    </>
  );
};
